import { useEffect, useMemo, useRef, useState } from "react";

import { encode } from "@nav-games/semaphore";

import { SemaphoreFigure } from "./SemaphoreFigure";

/// Type a message, see it played back as a sequence of semaphore
/// poses. Includes auto-play with a configurable interval (defaults
/// to 1 second per letter, slow enough for a beginner to read).

export function SemaphoreDemo() {
  const [text, setText] = useState("SOS HELLO");
  const [intervalMs, setIntervalMs] = useState(1000);
  const [playing, setPlaying] = useState(false);
  const [cursor, setCursor] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  const slots = useMemo(() => encode(text), [text]);

  // Drive the cursor when playing.
  useEffect(() => {
    if (!playing) return;
    if (slots.length === 0) {
      setPlaying(false);
      return;
    }
    timeoutRef.current = window.setTimeout(() => {
      setCursor((c) => {
        if (c + 1 >= slots.length) {
          setPlaying(false);
          return c;
        }
        return c + 1;
      });
    }, intervalMs);
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [playing, cursor, intervalMs, slots.length]);

  const start = () => {
    if (slots.length === 0) return;
    setCursor(0);
    setPlaying(true);
  };
  const stop = () => setPlaying(false);
  const step = (delta: number) => {
    setPlaying(false);
    setCursor((c) => Math.max(0, Math.min(slots.length - 1, c + delta)));
  };

  const current = slots[cursor] ?? null;

  return (
    <div className="card">
      <h2>Semaphore</h2>
      <p className="muted small">
        Type a message and step through it as flag positions. The figure
        faces you, so the sender's right arm appears on your left.
      </p>

      <label className="small muted" htmlFor="semaphore-input">
        Message
      </label>
      <input
        id="semaphore-input"
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setCursor(0);
          setPlaying(false);
        }}
        placeholder="Type a message — letters and spaces"
        style={{ marginTop: 4, marginBottom: 12 }}
      />

      <div className="row" style={{ marginBottom: 12, alignItems: "center", gap: 16 }}>
        <label className="small muted" htmlFor="semaphore-interval">
          Step (ms)
        </label>
        <input
          id="semaphore-interval"
          type="number"
          min={250}
          max={5000}
          step={100}
          value={intervalMs}
          onChange={(e) => setIntervalMs(Math.max(100, Number(e.target.value) || 1000))}
          style={{ width: 100 }}
        />
        <span className="small muted">
          {slots.length === 0 ? "(empty)" : `slot ${Math.min(cursor + 1, slots.length)}/${slots.length}`}
        </span>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        {playing ? (
          <button className="secondary" onClick={stop}>
            Pause
          </button>
        ) : (
          <button onClick={start} disabled={slots.length === 0}>
            Play
          </button>
        )}
        <button className="secondary" onClick={() => step(-1)} disabled={slots.length === 0 || cursor === 0}>
          ← Prev
        </button>
        <button
          className="secondary"
          onClick={() => step(1)}
          disabled={slots.length === 0 || cursor >= slots.length - 1}
        >
          Next →
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "center",
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: 16,
          minHeight: 200,
        }}
      >
        {current ? (
          <SemaphoreFigure
            pose={current.pose}
            caption={current.isWordBreak ? "(word break)" : (current.letter ?? "?")}
            size={180}
          />
        ) : (
          <span className="muted">(empty)</span>
        )}
        <Strip slots={slots} cursor={cursor} />
      </div>
    </div>
  );
}

/// Linear preview of the upcoming letters. Highlights the current
/// cursor. Helpful for the user to see what's coming up while watching
/// the big figure.
function Strip({
  slots,
  cursor,
}: {
  slots: ReturnType<typeof encode>;
  cursor: number;
}) {
  if (slots.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignContent: "flex-start",
        flex: 1,
      }}
    >
      {slots.map((slot, i) => (
        <span
          key={i}
          style={{
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            width: 28,
            height: 28,
            borderRadius: 4,
            background: i === cursor ? "var(--accent)" : "transparent",
            color: i === cursor ? "var(--accent-fg)" : "var(--text-muted)",
            border: i === cursor ? "none" : "1px solid var(--border)",
            fontFamily: "ui-monospace, monospace",
            fontSize: 13,
          }}
        >
          {slot.isWordBreak ? "·" : (slot.letter ?? "?")}
        </span>
      ))}
    </div>
  );
}
